import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import type { ReactNode } from 'react'
import { Accordion } from '../common/accordion'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogFooter,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Label, type LabelProps } from '../common/label'
import { Popover, PopoverContent } from '../common/popover'
import { Separator } from '../common/separator'
import { Tooltip, TooltipContent } from '../common/tooltip'
import { PresetSelect } from '../discoverOpportunities/presetSelect'
import { SavePresetDialog } from '../discoverOpportunities/savePresetDialog'
import { FilterIcon } from '../icons/filter'
import { HelpIcon } from '../icons/help'
import { PlusIcon } from '../icons/plus'
import type { VaultPresetsProps } from '../shared/vaultPresets'
import { ApyRangeFilter } from './apyRangeFilter'
import { CategoryFilter, CategoryFilterAccordion } from './categoryFilter'
import { CuratorFilter, CuratorFilterAccordion } from './curatorFilter'
import { FilterSelectTrigger } from './filterSelect'
import { NetworkFilter, NetworkFilterAccordion } from './networkFilter'
import { ProtocolFilter, ProtocolFilterAccordion } from './protocolFilter'
import { ReputationFilter, ReputationFilterAccordion } from './reputationFilter'
import { RewardsFilter } from './rewardsFilter'
import { SearchFilter } from './searchFilter'
import { SortSelect } from './sortSelect'
import { TokenFilter, TokenFilterAccordion } from './tokenFilter'
import { TransactionalFilter } from './transactionalFilter'
import { TvlFilter, TvlFilterAccordion } from './tvlFilter'

interface FiltersProps {
  vaults: Vault[]
  numberOfVaults?: number
  className?: string
}

interface DesktopFilterProps extends FiltersProps {
  areParamsModified: boolean
  leadingContent?: ReactNode
  onClearClick: () => void
}

export const VaultsFilters = ({
  vaults,
  numberOfVaults = vaults.length,
  className,
  areParamsModified,
  leadingContent,
  onClearClick,
}: DesktopFilterProps & VaultPresetsProps) => {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex justify-between gap-2">
        {leadingContent}
        <SearchFilter
          numberOfVaults={numberOfVaults}
          className="flex-grow"
          trackingParams={{ Page: 'Discover', Device: 'Desktop' }}
        />
        <div className="flex items-center gap-2 empty:hidden">
          {areParamsModified && (
            <Button
              variant="input"
              size="md"
              title="Clear filters"
              className="fade-in size-10 animate-in px-0"
              onClick={onClearClick}
            >
              <span className="sr-only">Clear filters</span>
              <PlusIcon size={20} className="rotate-45" />
            </Button>
          )}
          {areParamsModified && <SavePresetDialog />}
          <PresetSelect />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex w-fit flex-wrap items-center gap-2">
            <TokenFilter vaults={vaults} />
            <NetworkFilter vaults={vaults} />
            <ProtocolFilter vaults={vaults} />
            <CuratorFilter vaults={vaults} />
            <CategoryFilter vaults={vaults} />
            <ReputationFilter />
            <TvlFilter />
          </div>
          <div className="flex w-fit flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm">Show yield as avg. of</p>
              <ApyRangeFilter />
            </div>
            <Popover>
              <FilterSelectTrigger>
                <span>More</span>
              </FilterSelectTrigger>
              <PopoverContent className="flex w-auto flex-col gap-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="transactionalFilter" className="group flex cursor-pointer items-center gap-2 text-sm">
                    <TransactionalFilter size="sm" id="transactionalFilter" />
                    <span>Transactional only</span>
                  </Label>
                  <Tooltip className="rounded-full">
                    <HelpIcon />
                    <TooltipContent className="max-w-52">
                      <p>Show vaults with transactions supported directly inside the vaults.fyi app</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Label className="group flex cursor-pointer items-center gap-2 text-sm">
                  <RewardsFilter size="sm" />
                  <span>Include rewards</span>
                </Label>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FiltersModalProps {
  trackingParams?: Record<string, string>
  className?: string
}

export const VaultsFiltersModal = ({
  vaults,
  numberOfVaults = vaults.length,
  trackingParams,
  className,
}: FiltersProps & FiltersModalProps) => {
  const { trackEvent } = useMixpanel()
  const { touchedParamsCount, clearSearchState } = useVaultsSearchState()

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="default"
          className={cn('relative rounded-xl bg-page ring-inset', className)}
          onClick={() => trackEvent('Mobile filters used', { ...trackingParams })}
        >
          <FilterIcon size={24} />
          {touchedParamsCount > 0 && (
            <p className="pointer-events-none absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-center font-bold text-page leading-none ring-2 ring-page [font-size:10px]">
              <span className="sr-only">Number of filters applied applied</span>
              <span>{touchedParamsCount}</span>
            </p>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent position="right">
        <DialogBody className="bg-page">
          <DialogHeaderWrapper className="px-5 md:pt-8">
            <DialogHeader>
              <DialogTitle>Filters</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          <DialogScrollArea>
            <DialogContentWrapper className="px-5 pt-4">
              <div className="flex flex-wrap gap-2">
                <SearchFilter
                  className="w-fit flex-1 basis-48"
                  numberOfVaults={numberOfVaults}
                  trackingParams={{ Page: 'Discover', Device: 'Mobile' }}
                />
                <SortSelect className="flex-1 justify-start" modal />
              </div>
              <PresetSelect />
              <FilterLabel className="flex-wrap gap-y-1">
                Show yield as avg. of
                <ApyRangeFilter />
              </FilterLabel>
              <FilterLabel>
                Include rewards
                <RewardsFilter />
              </FilterLabel>
              <FilterLabel>
                <div className="flex items-center gap-1">
                  <span>Transactional only</span>
                  <Tooltip className="rounded-full">
                    <HelpIcon />
                    <TooltipContent className="max-w-52">
                      <p>Show vaults with transactions supported directly inside the vaults.fyi app</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TransactionalFilter />
              </FilterLabel>
              <Accordion type="multiple">
                <TokenFilterAccordion vaults={vaults} />
                <NetworkFilterAccordion vaults={vaults} />
                <ProtocolFilterAccordion vaults={vaults} />
                <CuratorFilterAccordion vaults={vaults} />
                <CategoryFilterAccordion vaults={vaults} />
                <ReputationFilterAccordion />
                <TvlFilterAccordion />
              </Accordion>
              {touchedParamsCount > 0 && (
                <div className="flex gap-2">
                  <Button variant="input" size="md" onClick={clearSearchState} className="gap-2">
                    <PlusIcon size={20} className="rotate-45" />
                    Clear filters
                  </Button>
                  <SavePresetDialog className="flex-1 gap-2" />
                </div>
              )}
            </DialogContentWrapper>
          </DialogScrollArea>
          <DialogFooter className="px-5">
            <DialogClose asChild>
              <Button variant="primary" size="md" className="w-full">
                Confirm
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

const FilterLabel = ({ className, ...props }: LabelProps) => (
  <Label className={cn('flex items-center justify-between gap-2 text-sm', className)} {...props} />
)
